import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { DollarSign, Download, FileSpreadsheet, RefreshCw, TrendingUp, Building2, Users } from 'lucide-react';

export default function CRARemittance() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [periodSummaries, setPeriodSummaries] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, periodsRes] = await Promise.all([
        api.get('/payroll/remittance/summary'),
        api.get('/payroll/remittance/by-pay-period')
      ]);
      setSummary(summaryRes.data);
      setPeriodSummaries(periodsRes.data);
    } catch (error) {
      console.error('Remittance fetch error:', error);
      toast.error('Failed to fetch remittance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdown = async (periodId) => {
    setBreakdownLoading(true);
    setSelectedPeriod(periodId);
    try {
      const res = await api.get(`/payroll/remittance/employee-breakdown/${periodId}`);
      setBreakdown(res.data);
    } catch (error) {
      toast.error('Failed to fetch breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const exportCSV = async (periodId = null) => {
    try {
      const url = periodId 
        ? `/payroll/remittance/export/csv/${periodId}`
        : '/payroll/remittance/export/summary/csv';
      
      const response = await api.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = periodId ? `remittance_${periodId}.csv` : 'remittance_summary.csv';
      link.click();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(val || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">CRA Remittance Tracking</h1>
          <p className="text-slate-500">Track payroll deductions and employer contributions</p>
        </div>
        <Button onClick={() => exportCSV()} variant="outline">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export All to CSV
        </Button>
      </div>

      {/* Overall Summary */}
      {summary && summary.summary !== null && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Gross Payroll</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(summary.gross_payroll)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Employee Deductions</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(summary.employee_deduction_subtotal)}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Employer Contributions</p>
                    <p className="text-xl font-bold text-purple-600">{formatCurrency(summary.employer_contribution_subtotal)}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">Total Remittance</p>
                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(summary.remittance_grand_total)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employee Deductions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">CPP</span>
                    <span className="font-medium">{formatCurrency(summary.employee_deductions.total_cpp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">CPP2</span>
                    <span className="font-medium">{formatCurrency(summary.employee_deductions.total_cpp2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">EI</span>
                    <span className="font-medium">{formatCurrency(summary.employee_deductions.total_ei)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Federal Tax</span>
                    <span className="font-medium">{formatCurrency(summary.employee_deductions.total_federal_tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Provincial Tax</span>
                    <span className="font-medium">{formatCurrency(summary.employee_deductions.total_provincial_tax)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employer Contributions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">CPP (1:1 match)</span>
                    <span className="font-medium">{formatCurrency(summary.employer_contributions.employer_cpp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">CPP2 (1:1 match)</span>
                    <span className="font-medium">{formatCurrency(summary.employer_contributions.employer_cpp2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">EI (1.4x employee)</span>
                    <span className="font-medium">{formatCurrency(summary.employer_contributions.employer_ei)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Pay Period Summaries */}
      <Card>
        <CardHeader>
          <CardTitle>Remittance by Pay Period</CardTitle>
          <CardDescription>Click a period to view employee breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          {periodSummaries.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No finalized payroll data available
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pay Period</TableHead>
                  <TableHead className="text-center">Employees</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">CPP (Emp + Empr)</TableHead>
                  <TableHead className="text-right">EI (Emp + Empr)</TableHead>
                  <TableHead className="text-right">Taxes</TableHead>
                  <TableHead className="text-right">Remittance Total</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {periodSummaries.map((period) => (
                  <TableRow 
                    key={period.pay_period_id}
                    className={selectedPeriod === period.pay_period_id ? 'bg-slate-50' : ''}
                  >
                    <TableCell>
                      <div className="font-medium">{period.pay_period_name}</div>
                      <div className="text-sm text-slate-500">{period.pay_period_start} - {period.pay_period_end}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{period.employee_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(period.gross_total)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(period.cpp_employee + period.cpp_employer)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(period.ei_employee + period.ei_employer)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(period.federal_tax + period.provincial_tax)}</TableCell>
                    <TableCell className="text-right font-bold text-emerald-600">{formatCurrency(period.remittance_total)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => fetchBreakdown(period.pay_period_id)}
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => exportCSV(period.pay_period_id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Employee Breakdown */}
      {breakdown && (
        <Card>
          <CardHeader>
            <CardTitle>Employee Breakdown - {breakdown.pay_period?.name}</CardTitle>
            <CardDescription>Detailed deductions per employee</CardDescription>
          </CardHeader>
          <CardContent>
            {breakdownLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">CPP (Emp)</TableHead>
                      <TableHead className="text-right">CPP (Empr)</TableHead>
                      <TableHead className="text-right">EI (Emp)</TableHead>
                      <TableHead className="text-right">EI (Empr)</TableHead>
                      <TableHead className="text-right">Fed Tax</TableHead>
                      <TableHead className="text-right">Prov Tax</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {breakdown.employees.map((emp) => (
                      <TableRow key={emp.employee_id}>
                        <TableCell className="font-medium">{emp.employee_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.gross_earnings)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.cpp_employee)}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(emp.cpp_employer)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.ei_employee)}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(emp.ei_employer)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.federal_tax)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.provincial_tax)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(emp.net_pay)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-bold">
                      <TableCell>TOTALS</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.gross)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.cpp_employee)}</TableCell>
                      <TableCell className="text-right text-purple-600">{formatCurrency(breakdown.totals.cpp_employer)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.ei_employee)}</TableCell>
                      <TableCell className="text-right text-purple-600">{formatCurrency(breakdown.totals.ei_employer)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.federal_tax)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.provincial_tax)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(breakdown.totals.net)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-700 font-medium">Total Remittance for this Pay Period</span>
                    <span className="text-2xl font-bold text-emerald-700">{formatCurrency(breakdown.remittance_total)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
